import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { X as XIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { STANDARD_PREVIEW_EVENTS, type PreviewEvent } from '@/services/creatives/previewEvents'
import { formatCount, formatNumber } from '@/utils/format'

type TypeFilter = 'all' | 'custom' | 'standard'
type ShowFilter = 'all' | 'triggered' | 'untriggered'

interface SummaryRow {
  key: string
  name: string
  custom: boolean
  count: number
  /** When it first fired, in ms since the creative started loading. */
  first?: number
}

function formatSeconds(ms: number): string {
  return formatNumber(ms / 1000, { style: 'unit', unit: 'second', maximumFractionDigits: 2 })
}

function eventName(t: TFunction, event: Pick<PreviewEvent, 'kind' | 'name'>): string {
  return event.kind === 'custom' ? (event.name ?? '') : t(`creatives.previewEvent.${event.kind}`)
}

function eventKey(event: Pick<PreviewEvent, 'kind' | 'name'>): string {
  return event.kind === 'custom' ? `custom:${event.name}` : event.kind
}

/**
 * What the previewed creative has fired: a summary per event with how often
 * and how soon, and the full log in the order the events came in.
 */
export function CreativePreviewEventPanel({ events }: { events: PreviewEvent[] }) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  const [showFilter, setShowFilter] = useState<ShowFilter>('all')

  const rows = useMemo(() => {
    const byKey = new Map<string, SummaryRow>()
    for (const kind of STANDARD_PREVIEW_EVENTS) {
      byKey.set(kind, { key: kind, name: eventName(t, { kind }), custom: false, count: 0 })
    }
    for (const event of events) {
      const key = eventKey(event)
      const row = byKey.get(key) ?? {
        key,
        name: eventName(t, event),
        custom: event.kind === 'custom',
        count: 0,
      }
      row.count += 1
      row.first = row.first ?? event.time
      byKey.set(key, row)
    }
    return [...byKey.values()]
  }, [events, t])

  const needle = search.trim().toLowerCase()
  const visible = rows.filter(
    (row) =>
      (!needle || row.name.toLowerCase().includes(needle)) &&
      (typeFilter === 'all' || row.custom === (typeFilter === 'custom')) &&
      (showFilter === 'all' || row.count > 0 === (showFilter === 'triggered'))
  )

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-4 md:grid-cols-4">
      <Card className="min-h-0 gap-3 md:col-span-3">
        <CardHeader>
          <CardTitle>{t('creatives.previewEventSummary')}</CardTitle>
          <CardDescription>
            {t('creatives.previewEventShowing', { shown: visible.length, total: rows.length })}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid min-h-0 grid-cols-1 gap-4 overflow-y-auto md:grid-cols-[16rem_1fr]">
          <div className="grid h-fit grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
            <Label htmlFor="preview-event-search" className="text-xs text-(--ad-text-muted)">
              {t('creatives.previewEventName')}
            </Label>
            <div className="relative">
              <Input
                id="preview-event-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pr-8"
              />
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute top-0 right-0 size-8"
                  onClick={() => setSearch('')}
                  aria-label={t('common.clear')}
                >
                  <XIcon className="size-3.5" />
                </Button>
              )}
            </div>

            <span className="text-xs text-(--ad-text-muted)">
              {t('creatives.previewEventType')}
            </span>
            <Tabs value={typeFilter} onValueChange={(v) => setTypeFilter(v as TypeFilter)}>
              <TabsList>
                <TabsTrigger value="all">{t('common.all')}</TabsTrigger>
                <TabsTrigger value="custom">{t('creatives.previewEventCustom')}</TabsTrigger>
                <TabsTrigger value="standard">{t('creatives.previewEventStandard')}</TabsTrigger>
              </TabsList>
            </Tabs>

            <span className="text-xs text-(--ad-text-muted)">
              {t('creatives.previewEventShow')}
            </span>
            <Tabs value={showFilter} onValueChange={(v) => setShowFilter(v as ShowFilter)}>
              <TabsList>
                <TabsTrigger value="all">{t('common.all')}</TabsTrigger>
                <TabsTrigger value="triggered">{t('creatives.previewEventTriggered')}</TabsTrigger>
                <TabsTrigger value="untriggered">
                  {t('creatives.previewEventUntriggered')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="min-w-0 overflow-y-auto">
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.name')}</TableHead>
                <TableHead>{t('creatives.previewEventType')}</TableHead>
                <TableHead className="text-right">{t('creatives.previewEventCount')}</TableHead>
                <TableHead className="text-right">{t('creatives.previewEventFirst')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="whitespace-normal">{row.name}</TableCell>
                  <TableCell
                    className={row.custom ? 'text-(--ad-primary)' : 'text-(--ad-text-muted)'}
                  >
                    {row.custom
                      ? t('creatives.previewEventCustom')
                      : t('creatives.previewEventStandard')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatCount(row.count)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {row.first === undefined ? '–' : formatSeconds(row.first)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card className="min-h-0 gap-3 md:col-span-1">
        <CardHeader>
          <CardTitle>{t('creatives.previewEventLog')}</CardTitle>
        </CardHeader>
        <CardContent className="min-h-0 overflow-y-auto">
          {events.length === 0 ? (
            <p className="text-sm text-(--ad-text-muted)">{t('creatives.previewEventLogEmpty')}</p>
          ) : (
            <ol className="flex flex-col gap-2 text-sm">
              {events.map((event, index) => (
                <li key={index} className="flex gap-3">
                  <span className="w-16 shrink-0 text-right tabular-nums text-(--ad-text-muted)">
                    {formatSeconds(event.time)}
                  </span>
                  <span className="min-w-0">
                    <span className="font-medium">{eventName(t, event)}</span>
                    {event.detail && (
                      <span
                        className="block truncate text-xs text-(--ad-text-muted)"
                        title={event.detail}
                      >
                        {event.detail}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
